import { ArrowRight, ArrowUpRight } from "lucide-react";
import ParallaxSection from "@/components/ParallaxSection";
import ImageSlider from "@/components/ImageSlider";
import BookingFields from "@/components/BookingFields";
import { useBooking } from "@/context/BookingContext";
import { rooms, roomAmenities, suiteAmenities, orchidPatternImg, IconGlyph } from "@/data";

function RoomHeading({ name }) {
  const words = name.split(" ");
  const lead = words.slice(0, -1).join(" ");
  const last = words.slice(-1);
  return (
    <h2>
      {lead ? `${lead} ` : ""}
      <em>{last}</em>
    </h2>
  );
}

function RoomAmenities({ extra }) {
  const items = extra ? [...roomAmenities, ...extra] : roomAmenities;
  return (
    <ul className="room-amenities">
      {items.map((a) => (
        <li className="room-amenity" key={a.label}>
          <span className="room-amenity-icon">
            <IconGlyph name={a.icon} />
          </span>
          {a.label}
        </li>
      ))}
    </ul>
  );
}

export default function Rooms() {
  const { openBooking } = useBooking();
  const [terra, cliffstone, horizon, orchid, grand] = rooms;

  return (
    <>
      {/* HERO */}
      <ParallaxSection image={grand.img} className="page-hero rm-hero" strength={16}>
        <div className="rm-hero-text">
          <p className="page-hero-eyebrow">
            <ArrowRight size={13} /> Rooms &amp; Suites
          </p>
          <h1>
            Five Private <em>Sanctuaries.</em>
          </h1>
          <p className="page-hero-sub">Five distinct room categories — each a private sanctuary.</p>
        </div>

        <aside className="booking-card fade-up" data-testid="rooms-booking-panel">
          <div className="booking-card-head">
            <h3>Booking Online</h3>
            <p>Check availability in seconds.</p>
          </div>
          <BookingFields onSubmit={openBooking} idPrefix="rooms" />
        </aside>
      </ParallaxSection>

      {/* INTRO */}
      <section className="rm-intro">
        <div className="rm-intro-copy fade-up">
          <p className="rm-intro-label">Rooms &amp; Suites</p>
          <p>
            Every room at The Vanda is a considered stay — natural textures, warm materials, generous windows, and calming interiors that feel effortlessly comfortable. Each one is named for the character it carries.
          </p>
          <span className="rm-intro-rule" aria-hidden="true" />
        </div>
        <div className="rm-intro-media">
          <ImageSlider
            images={[
              { src: terra.img, alt: `${terra.name} room at The Vanda` },
              { src: cliffstone.img, alt: `${cliffstone.name} room at The Vanda` },
              { src: horizon.img, alt: `${horizon.name} room at The Vanda` },
              { src: orchid.img, alt: `${orchid.name} room at The Vanda` },
              { src: grand.img, alt: `${grand.name} room at The Vanda` },
            ]}
            cycleSeconds={5}
          />
        </div>
      </section>

      {/* 01 — TERRA (EARTH) — full-bleed image, floating card */}
      <section className="rm-feature">
        <img className="rm-feature-bg" src={terra.img} alt={`${terra.name} room at The Vanda`} loading="lazy" />
        <div className="rm-feature-card fade-up">
          <RoomHeading name={terra.name} />
          <p>{terra.desc} {terra.longDesc}</p>
          <RoomAmenities />
          <a href="#book-now" onClick={openBooking} className="btn btn-outline" data-testid="room-cta-1">
            Check Availability <ArrowRight size={15} />
          </a>
        </div>
      </section>

      {/* 02 — CLIFFSTONE (ROCK) — text left, image + corner accent right */}
      <div className="room-band">
        <div className="room-band-copy fade-up">
          <RoomHeading name={cliffstone.name} />
          <p>{cliffstone.desc} {cliffstone.longDesc}</p>
          <RoomAmenities />
          <a href="#book-now" onClick={openBooking} className="btn btn-outline" data-testid="room-cta-2">
            Check Availability <ArrowRight size={15} />
          </a>
        </div>
        <div className="room-band-media rm-accent-media">
          <img src={cliffstone.img} alt={`${cliffstone.name} room at The Vanda`} loading="lazy" />
          <img src={orchidPatternImg} alt="" aria-hidden="true" className="rm-accent-corner" />
        </div>
      </div>

      {/* 03 — AIR (HORIZON) — image left, text right */}
      <div className="room-band">
        <div className="room-band-media">
          <img src={horizon.img} alt={`${horizon.tag} room at The Vanda`} loading="lazy" />
        </div>
        <div className="room-band-copy fade-up">
          <RoomHeading name={horizon.tag} />
          <p>{horizon.desc} {horizon.longDesc}</p>
          <RoomAmenities />
          <a href="#book-now" onClick={openBooking} className="btn btn-outline" data-testid="room-cta-3">
            Check Availability <ArrowRight size={15} />
          </a>
        </div>
      </div>

      {/* 04 — THE ORCHID (JUNIOR SUITE) — text, image */}
      <div className="room-band">
        <div className="room-band-copy fade-up">
          <RoomHeading name={orchid.name} />
          <p>{orchid.desc} {orchid.longDesc}</p>
          <RoomAmenities extra={suiteAmenities} />
          <a href="#book-now" onClick={openBooking} className="btn btn-outline" data-testid="room-cta-4">
            Check Availability <ArrowRight size={15} />
          </a>
        </div>
        <div className="room-band-media">
          <img src={orchid.img} alt={`${orchid.name} room at The Vanda`} loading="lazy" />
        </div>
      </div>

      {/* 05 — THE VANDA GRAND (PRESIDENTIAL SUITE) — image left, text right */}
      <div className="room-band">
        <div className="room-band-media">
          <img src={grand.img} alt={`${grand.name} room at The Vanda`} loading="lazy" />
        </div>
        <div className="room-band-copy fade-up">
          <RoomHeading name={grand.name} />
          <p>{grand.desc} {grand.longDesc}</p>
          <RoomAmenities extra={suiteAmenities} />
          <a href="#book-now" onClick={openBooking} className="btn btn-outline" data-testid="room-cta-5">
            Check Availability <ArrowRight size={15} />
          </a>
        </div>
      </div>

      <section className="inner-cta-band rm-cta">
        <img src={orchidPatternImg} alt="" aria-hidden="true" className="rm-cta-pattern" />
        <p className="eyebrow">
          <ArrowRight size={13} /> Find your room
        </p>
        <h2>
          Which one <em>carries your character?</em>
        </h2>
        <button onClick={openBooking} className="btn btn-outline light" data-testid="rooms-cta-book">
          Check Availability <ArrowUpRight size={17} />
        </button>
      </section>
    </>
  );
}
